import { AllEffect, CallEffect, ForkEffect, GetContextEffect, PutEffect, SelectEffect } from 'redux-saga/effects';
import { getContext, call, put, takeLatest, select, all, SagaGenerator } from 'typed-redux-saga';
import type { PayloadAction } from '@reduxjs/toolkit';
import { RobotScheduleActions } from './robotScheduleSlice';
import type {
  AssociateDevicesToMapErrorAction,
  AssociateDevicesToMapRequestAction,
  AssociateDevicesToMapSuccessAction,
  CreateScheduleErrorAction,
  CreateScheduleRequestAction,
  CreateScheduleSuccessAction,
  DeleteScheduleErrorAction,
  DeleteScheduleRequestAction,
  DeleteScheduleSuccessAction,
  ListAvailableMapGroupsErrorAction,
  ListAvailableMapGroupsRequestAction,
  ListAvailableMapGroupsSuccessAction,
  ListAvailableMapsErrorAction,
  ListAvailableMapsRequestAction,
  ListAvailableMapsSuccessAction,
  ListMapsErrorAction,
  ListMapsRequestAction,
  ListMapsSuccessAction,
  RobotScheduleListOccurrencesErrorAction,
  RobotScheduleListOccurrencesRequestAction,
  RobotScheduleListOccurrencesSuccessAction,
  RobotScheduleListRoutesErrorAction,
  RobotScheduleListRoutesRequestAction,
  RobotScheduleListRoutesSuccessAction,
  RobotScheduleListRoutinesErrorAction,
  RobotScheduleListRoutinesRequestAction,
  RobotScheduleListRoutinesSuccessAction,
  UpdateScheduleErrorAction,
  UpdateScheduleRequestAction,
  UpdateScheduleSuccessAction,
} from './robotScheduleActions.types';
import { selectRobotScheduleOccurrencesLastRequest } from './robotScheduleSelectors';
import type { IDependencies } from 'app/cross-cutting-concerns/dependency-injection/interfaces/IDependencies';
import type {
  AvailableMapsResponse,
  MapGroupsResponse,
  MapsResponse,
  RoutesResponse,
  RoutinesResponse,
  CleaningScheduleOccurrencesResponse,
} from 'app/modules/machine-inventory/ScheduleClient';
import type {
  AssociateDevicesToMapResponse,
  CleaningScheduleV2,
} from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';
import { toast } from 'app/cross-cutting-concerns/toasts/kcc/toast';

export function* listRoutinesSaga(
  action: RobotScheduleListRoutinesRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<RoutinesResponse>
  | PutEffect<RobotScheduleListRoutinesSuccessAction>
  | PutEffect<RobotScheduleListRoutinesErrorAction>,
  void,
  IDependencies
> {
  const { scheduleService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.listRoutines, action.payload);
    yield* put(RobotScheduleActions.robotScheduleListRoutinesSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.robotScheduleListRoutinesError({ error }));
  }
}

export function* listRoutesSaga(
  action: RobotScheduleListRoutesRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<RoutesResponse>
  | PutEffect<RobotScheduleListRoutesSuccessAction>
  | PutEffect<RobotScheduleListRoutesErrorAction>,
  void,
  IDependencies
> {
  const { scheduleService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.listRoutes, action.payload);
    yield* put(RobotScheduleActions.robotScheduleListRoutesSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.robotScheduleListRoutesError({ error }));
  }
}

export function* listMapsSaga(
  action: ListMapsRequestAction
): Generator<
  GetContextEffect | CallEffect<MapsResponse> | PutEffect<ListMapsSuccessAction> | PutEffect<ListMapsErrorAction>,
  void,
  IDependencies
> {
  const { scheduleService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.listMaps, action.payload);
    yield* put(RobotScheduleActions.listMapsSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.listMapsError({ error }));
  }
}

export function* listAvailableMapsSaga(
  action: ListAvailableMapsRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<AvailableMapsResponse>
  | PutEffect<ListAvailableMapsSuccessAction>
  | PutEffect<ListAvailableMapsErrorAction>,
  void,
  IDependencies
> {
  const { scheduleService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.listAvailableMaps, action.payload);
    yield* put(RobotScheduleActions.listAvailableMapsSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.listAvailableMapsError({ error }));
  }
}

export function* listAvailableMapGroupsSaga(
  action: ListAvailableMapGroupsRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<MapGroupsResponse>
  | PutEffect<ListAvailableMapGroupsSuccessAction>
  | PutEffect<ListAvailableMapGroupsErrorAction>,
  void,
  IDependencies
> {
  const { scheduleService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.listMapGroups, action.payload);
    yield* put(RobotScheduleActions.listAvailableMapGroupsSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.listAvailableMapGroupsError({ error }));
  }
}

export function* associateDevicesToMapSaga(
  action: AssociateDevicesToMapRequestAction
): Generator<
  | GetContextEffect
  | AllEffect<SagaGenerator<AssociateDevicesToMapResponse, CallEffect<AssociateDevicesToMapResponse>>>
  | PutEffect<AssociateDevicesToMapSuccessAction>
  | PutEffect<AssociateDevicesToMapErrorAction>
  | PutEffect<ListMapsRequestAction>,
  void,
  IDependencies
> {
  const { scheduleService, t } = yield* getContext<IDependencies>('dependencies');
  const { mapIds, materialNumber, serialNumber } = action.payload;

  try {
    const responses = yield* all(
      mapIds.map(mapId =>
        call(scheduleService.associateDevicesToMap, {
          id: mapId,
          associatedDevices: [{ materialNumber, serialNumber }],
        })
      )
    );

    const success = responses.every(response => response.success);

    if (!success) {
      throw new Error('Failed to associate devices to map');
    }

    yield* put(RobotScheduleActions.associateDevicesToMapSuccess({ success }));
    yield* put(
      RobotScheduleActions.listMapsRequest({ mnsns: [{ materialNumber, serialNumber }], filter: { limit: 10000 } })
    );

    toast.success({
      message: undefined,
      description: t('mapAssignmentModal.assignSuccessMessage') as string,
      closable: true,
    });
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.associateDevicesToMapError({ error }));

    toast.error({
      message: t('mapAssignmentModal.associateErrorMessage') as string,
      description: (error as Error).message,
    });
  }
}

export function* listOccurrencesSaga(
  action: RobotScheduleListOccurrencesRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<CleaningScheduleOccurrencesResponse>
  | PutEffect<RobotScheduleListOccurrencesSuccessAction>
  | PutEffect<RobotScheduleListOccurrencesErrorAction>,
  void,
  IDependencies
> {
  const { scheduleService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.listCleaningScheduleOccurrences, action.payload);
    yield* put(RobotScheduleActions.robotScheduleListOccurrencesSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.robotScheduleListOccurrencesError({ error }));
  }
}

export function* createScheduleSaga(
  action: CreateScheduleRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<CleaningScheduleV2 | void>
  | SelectEffect
  | PutEffect<CreateScheduleSuccessAction>
  | PutEffect<CreateScheduleErrorAction>
  | PutEffect<RobotScheduleListOccurrencesRequestAction>
  | PutEffect<PayloadAction>,
  void,
  IDependencies
> {
  const { scheduleService, t } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.createSchedule, action.payload);
    yield* put(RobotScheduleActions.createScheduleSuccess(response));

    toast.success({
      message: t('createSchedule.scheduleCreatedMessage') as string,
      description: t('createSchedule.scheduleCreatedDescription') as string,
    });

    const lastOccurrencesRequest = yield* select(selectRobotScheduleOccurrencesLastRequest);

    if (lastOccurrencesRequest) {
      yield* put(RobotScheduleActions.robotScheduleListOccurrencesRequest(lastOccurrencesRequest));
    }
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.createScheduleError({ error }));

    toast.error({
      message: t('createSchedule.scheduleCreatedError') as string,
      description: (error as Error).message,
    });
  }
}

export function* updateScheduleSaga(
  action: UpdateScheduleRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<CleaningScheduleV2 | void>
  | SelectEffect
  | PutEffect<UpdateScheduleSuccessAction>
  | PutEffect<UpdateScheduleErrorAction>
  | PutEffect<RobotScheduleListOccurrencesRequestAction>
  | PutEffect<PayloadAction>,
  void,
  IDependencies
> {
  const { scheduleService, t } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.updateSchedule, action.payload);
    yield* put(RobotScheduleActions.updateScheduleSuccess(response));

    toast.success({
      message: t('createSchedule.scheduleUpdatedMessage') as string,
      description: t('createSchedule.scheduleUpdatedDescription') as string,
    });

    const lastOccurrencesRequest = yield* select(selectRobotScheduleOccurrencesLastRequest);

    if (lastOccurrencesRequest) {
      yield* put(RobotScheduleActions.robotScheduleListOccurrencesRequest(lastOccurrencesRequest));
    }
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.updateScheduleError({ error }));

    toast.error({
      message: t('createSchedule.scheduleUpdatedError') as string,
      description: (error as Error).message,
    });
  }
}

export function* deleteScheduleSaga(
  action: DeleteScheduleRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<CleaningScheduleV2>
  | SelectEffect
  | PutEffect<DeleteScheduleSuccessAction>
  | PutEffect<DeleteScheduleErrorAction>
  | PutEffect<RobotScheduleListOccurrencesRequestAction>,
  void,
  IDependencies
> {
  const { scheduleService, t } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(scheduleService.deleteSchedule, action.payload);
    yield* put(RobotScheduleActions.deleteScheduleSuccess(response));

    toast.success({
      message: t('createSchedule.scheduleDeletedMessage') as string,
      description: t('createSchedule.scheduleDeletedDescription') as string,
    });

    const lastOccurrencesRequest = yield* select(selectRobotScheduleOccurrencesLastRequest);

    if (lastOccurrencesRequest) {
      yield* put(RobotScheduleActions.robotScheduleListOccurrencesRequest(lastOccurrencesRequest));
    }
  } catch (error) {
    console.error(error);
    yield* put(RobotScheduleActions.deleteScheduleError({ error }));

    toast.error({
      message: t('createSchedule.scheduleDeletedError') as string,
      description: (error as Error).message,
    });
  }
}

export function* robotScheduleSaga(): Generator<ForkEffect<never>, void> {
  yield* takeLatest(RobotScheduleActions.robotScheduleListRoutinesRequest, listRoutinesSaga);
  yield* takeLatest(RobotScheduleActions.robotScheduleListRoutesRequest, listRoutesSaga);
  yield* takeLatest(RobotScheduleActions.listMapsRequest, listMapsSaga);
  yield* takeLatest(RobotScheduleActions.robotScheduleListOccurrencesRequest, listOccurrencesSaga);
  yield* takeLatest(RobotScheduleActions.createScheduleRequest, createScheduleSaga);
  yield* takeLatest(RobotScheduleActions.updateScheduleRequest, updateScheduleSaga);
  yield* takeLatest(RobotScheduleActions.deleteScheduleRequest, deleteScheduleSaga);
  yield* takeLatest(RobotScheduleActions.listAvailableMapsRequest, listAvailableMapsSaga);
  yield* takeLatest(RobotScheduleActions.listAvailableMapGroupsRequest, listAvailableMapGroupsSaga);
  yield* takeLatest(RobotScheduleActions.associateDevicesToMapRequest, associateDevicesToMapSaga);
}
